import { useEffect, useState } from 'react';
import Sheet from './Sheet';
import Avatar from './Avatar';
import { PRIMARY } from './CaptureSheets';
import { addDays, longDate, shortDate, today } from '../lib/dates';
import { ABSENCE_REASONS, addMinutesToClock, atTime, clock, clockMinutes, localDate, weekdayShort } from '../lib/schedule';
import { requestReminderSync } from '../lib/reminders';
import { useApp } from '../lib/AppContext';

const CHIP = 'h-11 rounded-[12px] border px-3 text-sm font-semibold';
const chip = (on) => `${CHIP} ${on ? 'border-[#17804f] bg-[#e3f1e9] text-[#17804f]' : 'border-line bg-base-100'}`;
const LABEL = 'text-xs font-bold uppercase tracking-wide text-muted';
const dayChip = (iso) => `${weekdayShort(iso)} · ${shortDate(iso)}`;

// "Tập với Coach Minh · Thứ Tư 4/11 · hiện tại 18:00"
const subtitle = (a, extra = '') => `Tập với Coach ${a.coach_name} · ${longDate(localDate(a.starts_at)).replace(',', '')}${extra}`;

// The times offered for `date`: on another day the session's own time first, then an hour later, 90 minutes
// later and an hour earlier, staying within the day and, today, only times still ahead. At most three
// ("Khác" takes the fourth place).
const suggestedTimes = (hhmm, date, sessionDay) => {
  const shifts = date === sessionDay ? [60, 90, -60] : [0, 60, 90, -60];
  const nowClock = clock(new Date().toISOString());
  return [...new Set(shifts.map((m) => {
    const total = clockMinutes(hhmm) + m;
    return total >= 0 && total < 24 * 60 ? addMinutesToClock(hhmm, m) : null;
  }))].filter((t) => t && (date !== today() || t > nowClock)).slice(0, 3);
};

// B3 "Đề xuất đổi giờ": another day and time for one appointment, with an optional note. The coach approves or
// declines; until then the old time stands.
export function ProposalSheet({ appointment: a, onClose, onDone }) {
  const { api } = useApp();
  const [date, setDate] = useState('');
  const [otherDate, setOtherDate] = useState(false);
  const [time, setTime] = useState('');
  const [otherTime, setOtherTime] = useState(false);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  // Opens on the session's day with its first suggestion; when that day has no time left (a late session
  // today), on the next day at the same time.
  useEffect(() => {
    if (!a) return;
    const day = localDate(a.starts_at);
    const first = suggestedTimes(clock(a.starts_at), day, day)[0];
    setDate(first ? day : addDays(day, 1));
    setOtherDate(false);
    setTime(first || clock(a.starts_at));
    setOtherTime(false);
    setNote('');
    setError(null);
  }, [a]);
  if (!a) return <Sheet open={false} onClose={onClose} title="Đề xuất đổi giờ" />;

  const day = localDate(a.starts_at);
  const now = clock(a.starts_at);
  const times = date ? suggestedTimes(now, date, day) : [];
  const pickDate = (d) => {
    setDate(d);
    // A suggested time that the new day doesn't offer gives way to that day's first one.
    if (!otherTime && !suggestedTimes(now, d, day).includes(time)) setTime(suggestedTimes(now, d, day)[0] || '');
  };
  const changed = date && time && (date !== day || time !== now);
  const send = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const updated = await api.post(`/appointments/${a.id}/proposal`, { starts_at: atTime(date, time), note: note.trim() || null });
      requestReminderSync();
      onDone(updated, `Đã gửi đề xuất ${time} cho Coach ${a.coach_name}`);
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Sheet open onClose={onClose} title="Đề xuất đổi giờ" badge={<span className="text-xs text-muted">{subtitle(a, ` · hiện tại ${now}`)}</span>}>
      <form className="flex flex-col gap-4" onSubmit={send}>
        <div className="flex flex-col gap-1.5">
          <span className={LABEL}>Ngày</span>
          <div className="grid grid-cols-3 gap-2">
            {[day, addDays(day, 1)].map((d) => (
              <button key={d} type="button" className={chip(!otherDate && date === d)} onClick={() => { setOtherDate(false); pickDate(d); }}>{dayChip(d)}</button>
            ))}
            <button type="button" className={chip(otherDate)} onClick={() => setOtherDate(true)}>Ngày khác</button>
          </div>
          {otherDate && <input type="date" aria-label="Ngày khác" className="input h-12 w-full rounded-[14px] border-line text-base" min={today()}
            value={date} onChange={(e) => pickDate(e.target.value)} />}
        </div>
        <div className="flex flex-col gap-1.5">
          <span className={LABEL}>Giờ mới</span>
          {times.length === 0 && <p className="text-sm text-muted">Hôm nay không còn giờ phù hợp. Chọn ngày khác, hoặc bấm Khác để nhập giờ.</p>}
          <div className="grid grid-cols-4 gap-2">
            {times.map((t) => <button key={t} type="button" className={chip(!otherTime && time === t)} onClick={() => { setOtherTime(false); setTime(t); }}>{t}</button>)}
            <button type="button" className={chip(otherTime)} onClick={() => setOtherTime(true)}>Khác</button>
          </div>
          {otherTime && <input type="time" step={300} aria-label="Giờ khác" className="input h-12 w-full rounded-[14px] border-line text-base"
            value={time} onChange={(e) => setTime(e.target.value)} />}
        </div>
        <div className="flex flex-col gap-1.5">
          <label htmlFor="proposal-note" className={LABEL}>Lời nhắn cho Coach <span className="font-medium normal-case tracking-normal">(không bắt buộc)</span></label>
          <input id="proposal-note" className="input h-12 w-full rounded-[14px] border-line text-base" maxLength={200} placeholder="VD: Hôm đó em tan làm muộn ạ"
            value={note} onChange={(e) => setNote(e.target.value)} />
        </div>
        <p className="flex items-center justify-between rounded-[12px] bg-base-200 px-3 py-2 text-sm">
          <span className="text-muted">{weekdayShort(day)} {shortDate(day)} · {now}</span>
          <span aria-hidden="true">→</span>
          <b>{date ? `${weekdayShort(date)} ${shortDate(date)} · ${time || 'chọn giờ'}` : 'chọn ngày'}</b>
        </p>
        {error && <p role="alert" className="text-sm font-medium text-error">{error}</p>}
        <button type="submit" className={PRIMARY} disabled={!changed || busy}>
          {busy && <span className="loading loading-spinner loading-sm" />} Gửi đề xuất cho Coach {a.coach_name}
        </button>
        <p className="text-center text-xs text-muted">Lịch giữ giờ cũ cho đến khi Coach duyệt. Nhắc tập theo giờ đang có hiệu lực.</p>
      </form>
    </Sheet>
  );
}

// B6 "Báo nghỉ": an optional reason; the coach is told, and the two arrange a make-up session themselves.
export function AbsenceSheet({ appointment: a, onClose, onDone }) {
  const { api } = useApp();
  const [reason, setReason] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  useEffect(() => { setReason(null); setError(null); }, [a]);
  if (!a) return <Sheet open={false} onClose={onClose} title="Báo nghỉ" />;

  const isToday = localDate(a.starts_at) === today();
  const send = async () => {
    setBusy(true);
    setError(null);
    try {
      const updated = await api.post(`/appointments/${a.id}/absence`, { reason });
      requestReminderSync();
      onDone(updated, `Đã báo Coach ${a.coach_name}`);
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };
  return (
    <Sheet open onClose={onClose} title={isToday ? 'Nghỉ buổi hôm nay?' : `Nghỉ buổi ${weekdayShort(localDate(a.starts_at))} ${shortDate(localDate(a.starts_at))}?`}
      badge={<span className="text-xs text-muted">{subtitle(a, ` · ${clock(a.starts_at)}`)}</span>}>
      <div className="flex flex-col gap-4">
        <div className="flex flex-col gap-1.5">
          <span className={LABEL}>Lý do <span className="font-medium normal-case tracking-normal">(không bắt buộc)</span></span>
          <div className="flex flex-wrap gap-2" role="radiogroup" aria-label="Lý do">
            {ABSENCE_REASONS.map((r) => (
              <button key={r.id} type="button" role="radio" aria-checked={reason === r.id} className={`${chip(reason === r.id)} rounded-full`}
                onClick={() => setReason(reason === r.id ? null : r.id)}>{r.label}</button>
            ))}
          </div>
        </div>
        <p className="flex items-start gap-3 rounded-[14px] bg-base-200 p-3 text-sm">
          <Avatar name={a.coach_name} url={a.coach_avatar_url} size={28} />
          Coach {a.coach_name} sẽ nhận thông báo. Hai bạn tự liên hệ để sắp xếp buổi bù nếu cần.
        </p>
        {error && <p role="alert" className="text-sm font-medium text-error">{error}</p>}
        <button type="button" className="btn h-[54px] w-full rounded-2xl border-0 bg-[#17201b] text-base font-bold text-white" disabled={busy} onClick={send}>
          {busy && <span className="loading loading-spinner loading-sm" />} Báo nghỉ
        </button>
        <button type="button" className="mx-auto min-h-11 px-4 text-sm font-semibold" onClick={onClose}>Huỷ</button>
      </div>
    </Sheet>
  );
}
